import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { documentsApi, isCancelled } from './api';
import { ProductOption } from './types';

interface Props {
  /** Свободный текст: название / код / штрихкод / код маркировки */
  text: string;
  /** Точно выбранные позиции номенклатуры */
  products: ProductOption[];
  onChange: (next: { text: string; products: ProductOption[] }) => void;
  /** Источник подсказок (по умолчанию - товары документов в зоне видимости; «КМ'ы» подставляют свой) */
  search?: (q: string, signal: AbortSignal) => Promise<ProductOption[]>;
  /** Только точные позиции номенклатуры, без поиска по свободному тексту */
  productsOnly?: boolean;
}

type Item = ProductOption | string;

const fieldSx = { '& .MuiInputBase-root': { bgcolor: '#fff' } };
const MIN_QUERY = 2;

/**
 * Фильтр «Товар»: подсказки по номенклатуре (клик - точная позиция) и поиск по тексту (Enter / первая строка списка) -
 * по названию, коду, штрихкоду или коду маркировки (Data Matrix).
 */
const defaultSearch = (q: string, signal: AbortSignal) => documentsApi.products(q, signal);

const ProductFilter: React.FC<Props> = ({ text, products, onChange, search = defaultSearch, productsOnly = false }) => {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<ProductOption[]>([]);
  const [loading, setLoading] = useState(false);

  const query = input.trim();

  useEffect(() => {
    if (query.length < MIN_QUERY) { setOptions([]); setLoading(false); return undefined; }
    const ctrl = new AbortController();
    setLoading(true);
    const timer = window.setTimeout(() => {
      search(query, ctrl.signal)
        .then(list => { setOptions(list); setLoading(false); })
        .catch(err => { if (!isCancelled(err)) { setOptions([]); setLoading(false); } });
    }, 300);
    return () => { window.clearTimeout(timer); ctrl.abort(); };
  }, [query, search]);

  const value: Item[] = [...(text ? [text] : []), ...products];
  const listed: Item[] = query.length >= MIN_QUERY ? [...(productsOnly ? [] : [query]), ...options] : [];

  return (
    <Autocomplete<Item, true, false, true>
      multiple freeSolo size="small" limitTags={2} disableCloseOnSelect
      options={listed}
      value={value}
      inputValue={input}
      onInputChange={(_, v, reason) => { if (reason !== 'reset') setInput(v); }}
      filterOptions={x => x}
      loading={loading}
      loadingText="Поиск…"
      noOptionsText="Ничего не найдено"
      getOptionLabel={o => (typeof o === 'string' ? `«${o}»` : o.name)}
      isOptionEqualToValue={(a, b) => (typeof a === 'string' || typeof b === 'string' ? a === b : a.id === b.id)}
      onChange={(_, v) => {
        const texts = v.filter((x): x is string => typeof x === 'string');
        onChange({
          text: texts.length ? texts[texts.length - 1].trim() : '',
          products: v.filter((x): x is ProductOption => typeof x !== 'string')
        });
        setInput('');
      }}
      renderOption={(props, o) => (
        <li {...props} key={typeof o === 'string' ? '__text__' : o.id}>
          {typeof o === 'string' ? (
            <Typography variant="body2">Искать по тексту: <b>{o}</b></Typography>
          ) : (
            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 1.5, alignItems: 'baseline' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>{o.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[o.code && `код ${o.code}`, o.barcode].filter(Boolean).join(' · ') || '-'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {o.docs.toLocaleString('ru-RU')} док.
              </Typography>
            </Box>
          )}
        </li>
      )}
      renderInput={params => (
        <TextField
          {...params} label="Товар" sx={fieldSx}
          placeholder={value.length ? '' : (productsOnly ? 'Название, код или штрихкод' : 'Название, код, штрихкод или код маркировки')}
          helperText={productsOnly ? 'Введите минимум 2 символа и выберите позицию' : 'Клик по позиции - точный товар, Enter - поиск по тексту'}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
    />
  );
};

export default ProductFilter;
